import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppDispatch';
import { logout } from '../../store/slices/authSlice';
import { useIsPremium } from '../../hooks/usePremium';

const NAV = [
  { path: '/dashboard',     icon: '🏠', label: 'Главная',      premium: true  },
  { path: '/menu',          icon: '📋', label: 'Меню',         premium: false },
  { path: '/recipes',       icon: '📖', label: 'Рецепты',      premium: false },
  { path: '/family',        icon: '👨‍👩‍👧', label: 'Семья',        premium: false },
  { path: '/diary',         icon: '📓', label: 'Дневник',      premium: false },
  { path: '/fridge',        icon: '🧊', label: 'Холодильник',  premium: true  },
  // MG_MYPRODUCTS: продукты семьи, заведённые вручную. Premium — потому что
  // заводить их умеет только он (POST /fridge/products/ под тем же гейтом).
  { path: '/products',      icon: '🥑', label: 'Мои продукты', premium: true  },
  { path: '/shopping',      icon: '🛒', label: 'Покупки',      premium: false },
  // MG_SPECINVITE: кто имеет доступ к данным семьи — и как его прекратить
  { path: '/my-specialists', icon: '🩺', label: 'Специалисты', premium: false },
  { path: '/subscriptions', icon: '💳', label: 'Подписка',     premium: false },
  { path: '/profile',       icon: '👤', label: 'Профиль',      premium: false },
];

interface SidebarProps {
  /** MG_WEBMOBILE: закрыть выдвижную панель после перехода. На широком экране
      её никто не открывал — колбэк тогда просто ничего не делает. */
  onNavigate?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ onNavigate }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const isPremium = useIsPremium();
  // Free-юзеру premium-пункты не показываем вовсе (страницы не грузятся).
  const items = NAV.filter((n) => !n.premium || isPremium);
  // MG_CONSTRUCTOR: конструктор — только специалистам/стаффу.
  const isSpecialist = !!(user && (user.is_staff || user.is_specialist));

  return (
    <aside className="w-56 min-h-screen bg-sidebar text-sidebar-fg flex flex-col">
      {/* MG_SKIN: слот логотипа (заменить на <Logo/> белой версией, когда придёт
          ассет: logo_full_white ~h-7, читаемый на тёмном сайдбаре). */}
      <div className="px-6 py-5 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🍅</span>
          <span className="font-bold text-lg text-sidebar-fg">MenuGen</span>
        </div>
        {user && (
          <p className="text-xs text-sidebar-muted mt-1 truncate">{user.name}</p>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {items.map(({ path, icon, label }) => (
          <NavLink
            key={path}
            to={path}
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                'flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors',
                isActive
                  ? 'bg-primary/20 text-primary font-semibold'
                  : 'text-sidebar-muted hover:bg-white/5 hover:text-sidebar-fg',
              ].join(' ')
            }
          >
            <span className="text-base">{icon}</span>
            {label}
          </NavLink>
        ))}
        {/* MG_SPECINVITE: кабинет специалиста. Раньше ссылки не было вовсе —
            попасть в него можно было только набрав /specialist руками. */}
        {isSpecialist && (
          <NavLink
            to="/specialist"
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                'flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors',
                isActive
                  ? 'bg-primary/20 text-primary font-semibold'
                  : 'text-sidebar-muted hover:bg-white/5 hover:text-sidebar-fg',
              ].join(' ')
            }
          >
            <span className="text-base">🩺</span>
            Кабинет специалиста
          </NavLink>
        )}
        {/* MG_CONSTRUCTOR: пункт только для специалистов/стаффа */}
        {isSpecialist && (
          <NavLink
            to="/constructor"
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                'flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors',
                isActive
                  ? 'bg-primary/20 text-primary font-semibold'
                  : 'text-sidebar-muted hover:bg-white/5 hover:text-sidebar-fg',
              ].join(' ')
            }
          >
            <span className="text-base">🛠️</span>
            Конструктор
          </NavLink>
        )}
      </nav>

      {/* Logout + юридические ссылки */}
      <div className="px-3 py-4 border-t border-white/10">
        <button
          onClick={() => dispatch(logout())}
          className="flex items-center gap-3 px-3 py-2 w-full rounded-xl text-sm text-sidebar-muted hover:bg-white/5 hover:text-sidebar-fg transition-colors"
        >
          <span>🚪</span> Выйти
        </button>
        {/* MG_LEGAL */}
        <div className="mt-2 px-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-sidebar-muted">
          <NavLink to="/requisites" onClick={onNavigate} className="hover:text-sidebar-fg">Реквизиты</NavLink>
          <NavLink to="/offer" onClick={onNavigate} className="hover:text-sidebar-fg">Оферта</NavLink>
          {/* MG_PRIVACY */}
          <NavLink to="/privacy" onClick={onNavigate} className="hover:text-sidebar-fg">Политика ПД</NavLink>
        </div>
      </div>
    </aside>
  );
};
